import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as CopyIcon } from '../../../assets/icons/copy.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as SyncIcon } from '../../../assets/icons/sync.svg';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { TariffInterface } from '../../../interface/tariff.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableTariffFilterApplied,
  fetchAllTariffs,
  fetchTariffCounts,
  fetchTariffFilters,
  setSortData,
  updateTariffFilters,
} from '../../../redux/slices/TariffSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import TariffSidePanel from '../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import tariffApi from '../../../utils/api/tariff';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Component for displaying global tariffs
 * @returns {any} Global tariffs component
 */
function Global() {
  const { t } = useTranslation('tariff');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    tariffs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    tariffCounts,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.tariff);

  const [searchValue, setSearchValue] = useState('');
  const searchTimerReference = useRef<NodeJS.Timeout | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    notificationModal: false,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    sessions: tariffs,
  });

  /**
   * Table row click handler to open side panel
   * @param {string} id - The tariff ID
   */
  const handleClickTableRow = (id: string) => {
    searchParameters.set('tariffId', id);
    setSearchParameters(searchParameters);
  };

  /**
   * Fetch data
   * @param {any} data - Data to fetch
   */
  const fetchData = (data: any) => {
    dispatch(fetchAllTariffs({ ...data, type: 'global' }));
  };

  /**
   * Handle page nate limit change
   * @param {any} value - Value to change
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        ...value,
        searchBy: searchValue.trim() || undefined,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Handle sort function
   * @param {any} sortData - Data to sort
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        searchBy: searchValue.trim() || undefined,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiParameters.page,
        limit,
      }),
    );
  };

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        searchBy: searchValue.trim() || undefined,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  useEffect(() => {
    return () => {
      if (searchTimerReference.current) {
        clearTimeout(searchTimerReference.current);
      }
    };
  }, []);

  /**
   * Handle search with debounce
   * @param {string} name - Name to search
   * @param {string} value - Value to search
   */
  const handleSearch = (name: string, value: string) => {
    setSearchValue(value);
    if (searchTimerReference.current) {
      clearTimeout(searchTimerReference.current);
    }
    searchTimerReference.current = setTimeout(() => {
      const trimmedValue = value.trim();
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData(
        removeEmptyKeys({
          ...queryParameters,
          searchBy: trimmedValue,
          page: DefaultApiParameters.page,
          limit,
          sortField,
          sortBy,
        }),
      );
    }, 400);
  };

  /**
   * Handles clearing filters and resetting the search input
   */
  const handleClearFilter = () => {
    if (searchTimerReference.current) {
      clearTimeout(searchTimerReference.current);
    }
    setSearchValue('');
  };

  /**
   * Updates the availability status of a device in the local state
   * @param {string} tariffId - The ID of the device to update
   * @param {boolean} isAvailable - The new availability status
   */
  const updateAvailability = (tariffId: string, isAvailable: boolean) => {
    tariffApi
      .editAvailabilityTariff(tariffId, { isActive: isAvailable })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('statusChangedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({
                  page,
                  limit, // Need to fetch the current page
                });
                dispatch(fetchTariffCounts('global'));
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Failed to create tariff',
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {any} tariff - The tariff to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (tariff: TariffInterface, checked: boolean) => {
    if (checked) {
      updateAvailability(tariff._id, checked);
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        rightCorner: '',
        content: (
          <DisableSwitchWarning
            selectedId={tariff._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableTariffConfirmation')}
            onConfirm={() => updateAvailability(tariff._id, checked)}
          />
        ),
        notificationModal: true,
      });
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {any} tariff - The tariff data
   * @returns {any} The rendered component
   */
  const renderAvailability = (tariff: TariffInterface) => (
    <div
      role="presentation"
      className="flex justify-center"
      onClick={(even) => even.stopPropagation()}>
      {tariff.status !== 'expired' && (
        <div key={tariff.id} className="availability-switch">
          <SwitchButton
            checked={tariff.isActive}
            change={(checked: boolean) => {
              if (!permissionCheck('cpoTariffStatusUpdate')) {
                return;
              }
              handleAvailabilityChange(tariff, checked);
            }}
          />
        </div>
      )}
    </div>
  );

  /**
   * Get localized status label
   * @param {string} status - Status value
   * @returns {string} Localized status text
   */
  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return t('active');
      case 'inactive':
        return t('inactive');
      case 'expired':
        return t('expired');
      case 'pending':
        return t('pending');
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
    }
  };

  /**
   * Syncs the availability status of a device with the backend
   * @param {string} tariffId - The tariff ID
   */
  const syncAvailabilityStatus = (tariffId: string) => {
    tariffApi
      .syncAvailabilityStatus(tariffId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('availabilityStatusSyncedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({
                  page,
                  limit, // Need to fetch the current page
                });
                dispatch(fetchTariffCounts('global'));
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('failedToSyncAvailabilityStatus'),
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle delete warning
   * @param {any} rowData - Data to delete
   * @param {any} event - Event to delete
   */
  const handleDeleteWarning = (rowData: TariffInterface, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      notificationModal: true,
      content: (
        <div className="delete-rating-modal">
          <h4>{t('deleteTariffModalTitle')}</h4>
          <p>{t('deleteTariffConfirmation')}</p>
          <span>{t('actionCannotBeUndone')}</span>
          <div className="delete-modal-actions">
            <MyButton
              label={t('cancel')}
              buttonType="outline"
              labelColorFill="6C757D"
              onClickFunc={() => setModalBoxStatus('close')}
            />
            <MyButton
              label={t('delete')}
              buttonType="error"
              labelColorFill="#ffffff"
              onClickFunc={() => {
                tariffApi
                  .deleteTariff(rowData._id)
                  .then(() => {
                    setModalBoxStatus('open');
                    setModalBoxDetail({
                      title: '',
                      rightCorner: '',
                      content: (
                        <SuccessNotification
                          title={t('tariffDeletedSuccessfully')}
                          description=""
                          notificationCloseTimer={3000}
                          onNotificationClose={() => {
                            setModalBoxStatus('close');
                            fetchData({
                              page,
                              limit, // Need to fetch the current page
                            });
                            dispatch(fetchTariffCounts('global'));
                          }}
                        />
                      ),
                      notificationModal: true,
                    });
                  })
                  .catch((error) => {
                    setModalBoxStatus('close');
                    dispatch(
                      setNotificationMessage({
                        status: true,
                        message: error?.response?.data?.message || 'Failed to create tariff',
                        type: 'error',
                        code: error?.response?.data?.code,
                      }),
                    );
                  });
              }}
            />
          </div>
        </div>
      ),
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(
      removeEmptyKeys({
        page: DefaultApiParameters.page,
        limit,
        ...queryParameters,
        searchBy: searchValue.trim() || undefined,
        sortField,
        sortBy,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData(
        removeEmptyKeys({
          page: DefaultApiParameters.page,
          limit,
          ...queryParameters,
          searchBy: searchValue.trim() || undefined,
          sortField,
          sortBy,
        }),
      );
      dispatch(disableTariffFilterApplied());
    }
  }, [isFilterUpdated]);

  const column = [
    {
      label: t('tariffName'),
      accessorKey: 'name' as keyof TariffInterface,
      sortAllow: true,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel isPartner={false} />,
      /**
       * Custom cell for scope name
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for scope name
       */
      customData: (currentData: TariffInterface) => (
        <div className="scope-name-cell">
          <div className="name">{currentData.name}</div>
          {/* <div className="id">ID: {currentData.scopeId}</div> */}
        </div>
      ),
    },
    // {
    //   label: 'Scope Level',
    //   accessorKey: 'scopeLevels' as keyof TariffInterface,
    //   sortAllow: true,
    //   /**
    //    * Custom cell for scope name
    //    * @param currentData Tariff interface
    //    * @returns Custom cell for scope name
    //    */
    //   customData: (currentData: TariffInterface) => (
    //     <div className="scope-name-cell">{currentData.scopeLevels.length}</div>
    //   ),
    // },
    {
      label: t('currentType'),
      accessorKey: 'currentType' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for current type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for current type
       */
      customData: (currentData: TariffInterface) => (
        <div className={currentData?.currentType === 'AC' ? 'ac-status' : 'dc-status'}>
          {currentData?.currentType}
        </div>
      ),
    },
    {
      label: t('connectorRatingKw'),
      accessorKey: 'connectorRating' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for current type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for current type
       */
      customData: (currentData: TariffInterface) => (
        <div>
          {currentData.connectorRating.currentType}{' '}
          {ToFixedValue(currentData.connectorRating.capacityKW)} {currentData.connectorRating.unit}
        </div>
      ),
    },
    // {
    //   label: 'Time Based Pricing Slots',
    //   accessorKey: 'isTimeBasedPricingEnabled' as keyof TariffInterface,
    //   sortAllow: true,
    //   /**
    //    * Custom cell for time based pricing slots
    //    * @param {any} currentData - Tariff interface
    //    * @returns {any} Custom cell
    //    */
    //   customData: (currentData: TariffInterface) => (
    //     <div
    //       className={currentData?.isTimeBasedPricingEnabled ? 'active-status' : 'inactive-status'}>
    //       <div className="status-dot" />
    //       {currentData?.isTimeBasedPricingEnabled ? 'Yes' : 'No'}
    //     </div>
    //   ),
    // },
    {
      label: t('status'),
      accessorKey: 'status' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for status
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for status
       */
      customData: (currentData: TariffInterface) => (
        <div className={`${currentData.status}-status`}>
          <div className="status-dot" /> {getStatusLabel(currentData.status)}
        </div>
      ),
    },
    {
      label: t('enforcementDate'),
      accessorKey: 'effectiveFrom' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for current type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for current type
       */
      customData: (currentData: TariffInterface) => (
        <div>{ConvertUTCtoDateTime(currentData.effectiveFrom)}</div>
      ),
    },
    {
      label: t('expiryDate'),
      accessorKey: 'effectiveTo' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for current type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for current type
       */
      customData: (currentData: TariffInterface): any => (
        <div>{ConvertUTCtoDateTime(currentData.effectiveTo)}</div>
      ),
    },
    {
      label: t('availability'),
      accessorKey: 'isAvailable' as keyof TariffInterface,
      customData: renderAvailability,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('actions'),
      accessorKey: 'actions' as keyof TariffInterface,
      sortAllow: false,
      sticky: 'right' as const,
      /**
       * Custom cell for actions
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for actions
       */
      customData: (currentData: TariffInterface): any => (
        <div
          className="action-button-group"
          role="presentation"
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
          }}>
          {currentData.status !== 'expired' && (
            <div
              className={`action-button-item edit-color ${
                !(permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate'))
                  ? 'disable-action'
                  : ''
              }`}
              role="presentation"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (!(
                  permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate')
                )) {
                  return;
                }

                navigate(`/ev-admin/tariffs/edit-tariff/${currentData.id}`);
              }}>
              <EditIcon style={{ color: 'var(--primary-color)' }} />
            </div>
          )}

          <div
            className={`action-button-item edit-color ${
              !(permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate'))
                ? 'disable-action'
                : ''
            }`}
            role="presentation"
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              if (!(permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate'))) {
                return;
              }

              navigate(`/ev-admin/tariffs/clone-tariff/${currentData.id}`);
            }}>
            <CopyIcon style={{ color: 'var(--primary-color)' }} />
          </div>
          <div
            className={`action-button-item delete-color ${
              !permissionCheck('partnerTariffDelete') ? 'disable-action' : ''
            }`}
            role="presentation"
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              if (!(permissionCheck('partnerTariffDelete') || permissionCheck('cpoTariffUpdate'))) {
                return;
              }
              handleDeleteWarning(currentData, event);
            }}>
            <DeleteIcon style={{ color: 'var(--error)' }} />
          </div>
          {currentData.status === 'pending' && new Date(currentData.effectiveFrom) < new Date() && (
            <div
              className={`action-button-item warning-color ${
                !permissionCheck('partnerTariffUpdate') ? 'disable-action' : ''
              }`}
              role="presentation"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (!(
                  permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffStatusUpdate')
                )) {
                  return;
                }
                syncAvailabilityStatus(currentData.id);
              }}>
              <SyncIcon style={{ color: 'var(--warning)' }} />
            </div>
          )}
        </div>
      ),
    },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('tariffName'), accessorKey: 'name' },
    { label: t('currentType'), accessorKey: 'currentType' },
    { label: t('connectorRatingKw'), accessorKey: 'connectorRating' },
    { label: t('status'), accessorKey: 'status' },
    { label: t('enforcementDate'), accessorKey: 'effectiveFrom' },
    { label: t('expiryDate'), accessorKey: 'effectiveTo' },
    { label: t('availability'), accessorKey: 'isAvailable' },
    { label: t('createdAt'), accessorKey: 'createdAt' },
    { label: t('updatedAt'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - Export type (pdf/csv)
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
      type: 'global',
      searchBy: searchValue,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    tariffApi
      .exportTariffData(removeEmptyKeys(payload))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: TariffInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'name':
                return row.name || 'N/A';
              case 'connectorRating':
                return (
                  `${row.connectorRating.currentType} ${ToFixedValue(row.connectorRating.capacityKW)} ${row.connectorRating.unit}` ||
                  'N/A'
                );
              case 'isAvailable': {
                let availability;
                if (row.status !== 'expired') {
                  return row.isActive ? 'Available' : 'Unavailable';
                }
                return availability;
              }
              case 'status':
                return row.isActive ? t('active') : t('inactive');
              case 'effectiveFrom':
                return ConvertUTCtoDateTime(row.effectiveFrom) || 'N/A';
              case 'effectiveTo':
                return ConvertUTCtoDateTime(row.effectiveTo) || 'N/A';
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt || '-');
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt || '-');
              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'global-tariff-list.csv');
          } else {
            exportPDF(headers, rows, 'global-tariff-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  const leftTopContent = (
    <div className="tariff-table-header-left">
      <CustomInput
        placeholder={t('search')}
        name="search"
        type="text"
        value={searchValue}
        onChangeValue={(name, value) => handleSearch(name, String(value))}
        svgIcon={<SearchIcon className="tariff-search-icon" />}
      />
    </div>
  );

  const topLeftContent = (
    <div className="connector-rating-list-top-left-content">
      <div className="counts">
        <p className="count">{tariffCounts.active + tariffCounts.inactive || 0}</p>
      </div>

      <div className="status active">
        <div className="indicator" />
        <p className="label">{t('active')}</p>
        <p className="count">{tariffCounts?.active || 0}</p>
      </div>

      <div className="status inactive">
        <div className="indicator" />
        <p className="label">{t('inactive')}</p>
        <p className="count">{tariffCounts?.inactive || 0}</p>
      </div>
    </div>
  );

  useEffect(() => {
    dispatch(fetchTariffCounts('global'));
  }, [dispatch]);

  return (
    <div className="global-tariff-wrapper">
      <ModalBox
        open={modalBoxStatus === 'open'}
        header={false}
        title={modalBoxDetail.title}
        notification={modalBoxDetail.notificationModal}
        rightCorner={modalBoxDetail.rightCorner}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
      />
      <PageTitle title={t('totalChargingTariffs')} topLeftContent={topLeftContent} />

      <CustomTable
        dataKey="globalTariff"
        header={column}
        data={tariffs}
        version="v2"
        disableSearch
        leftTopContent={leftTopContent}
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        actionButton
        sizeChangeButtonRequired
        exportDataPdfOrCSV={
          permissionCheck('partnerTariffExport') || permissionCheck('cpoTariffCalculation')
            ? exportDataPdfOrCSV
            : undefined
        }
        onHiddenColumnsChange={setHiddenColumns}
        exportLoader={exportLoader}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        filters={filters}
        serialNo
        created
        lastUpdated
        height={405}
        filtersToDispatch={updateTariffFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchTariffFilters}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        rowClickEvent={handleClickTableRow}
        rowClickSidePanel={<TariffSidePanel isPartner={false} />}
        rowClickParamsKey="tariffId"
        onSidePanelClose={handleSidePanelClose}
        refreshPosition="left"
        onClearFilterChange={handleClearFilter}
      />
    </div>
  );
}

export default Global;
